import { FC, useContext, useState } from "react";
import { Controller } from "react-hook-form";
import NotificationDispatch, {
  showErrorNotification,
  showSuccessNotification,
} from "../../../../../../context/notificationContext";
import useI18n from "../../../../../../hooks/useTranslations";
import { Shuttle } from "../../../../../../http/vertigrip/types/shuttles";
import { useShuttlesApi } from "../../../../../../http/vertigrip/useShuttles";
import Button from "../../../../../ui/Button";
import Form from "../../../../../ui/Form";
import FormField from "../../../../../ui/FormField";
import ModalDialog from "../../../../../ui/ModalDialog";
import Numeric from "../../../../../ui/Numeric";
import VisibilityScope, { VisibilityScopeValue } from "../../../../../ui/VisibilityScope";

interface shuttleElementForm {
  order: number | null;
  visibility: VisibilityScopeValue;
}

interface EditShuttleModalDialogProps {
  shuttle?: Shuttle;
  onClose: () => void;
}

const EditShuttleModalDialog: FC<EditShuttleModalDialogProps> = ({ shuttle, onClose }: EditShuttleModalDialogProps) => {
  const i18n = useI18n();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const dispatch = useContext(NotificationDispatch);
  const shuttlesApi = useShuttlesApi();

  return (
    <>
      {!!shuttle && (
        <ModalDialog onClose={() => onClose()} title={i18n.translation.shuttles.edit}>
          <Form<shuttleElementForm>
            defaultValues={{
              order: shuttle?.order ?? 50,
              visibility: shuttle?.visibility,
            }}
            submitText={i18n.translation.common.save}
            disabled={isSubmitting}
            secondaryButton={
              <Button buttonProps={{ onClick: () => onClose() }} type="secondary">
                {i18n.translation.common.cancel}
              </Button>
            }
            onSubmit={() => (formData) =>
              shuttlesApi
                .updateAdminShuttle(shuttle?.id!, {
                  order: formData.order!,
                  systemTypeId: shuttle?.systemType.id!,
                  visibility: formData.visibility,
                })
                .then(() => {
                  onClose();
                  dispatch(showSuccessNotification(i18n.translation));
                })
                .catch((err) => {
                  dispatch(showErrorNotification(i18n, err));
                  setIsSubmitting(false);
                })
            }
          >
            {({ control, setValue }) => {
              return (
                <>
                  <Controller
                    name="visibility"
                    control={control}
                    rules={{ required: true }}
                    render={({ field, fieldState }) => (
                      <FormField label={i18n.translation.common.visibility} error={fieldState.error} isOptional>
                        {({ labelId }) => (
                          <VisibilityScope
                            {...field}
                            inputId={labelId}
                            onChange={(value) => setValue("visibility", value)}
                          />
                        )}
                      </FormField>
                    )}
                  />
                  <Controller
                    name="order"
                    control={control}
                    rules={{ required: true, min: 1 }}
                    render={({ field, fieldState }) => (
                      <FormField label={i18n.translation.common.order} error={fieldState.error}>
                        {({ labelId, isInvalid }) => (
                          <Numeric
                            {...field}
                            id={labelId}
                            isInvalid={isInvalid}
                            decimalScale={0}
                            allowNegative={false}
                            onBlur={(value) => setValue("order", value)}
                          />
                        )}
                      </FormField>
                    )}
                  />
                </>
              );
            }}
          </Form>
        </ModalDialog>
      )}
    </>
  );
};

export default EditShuttleModalDialog;
